import React, { useMemo } from 'react';

import {
	buildInterchangeBreakdown,
	CARD_TYPES,
	effectiveRate,
} from '../../utils/interchangeBreakdown';
import { formatCurrency } from '../../utils/utils';

import type { UnifiedFeeItem } from '../../types';

interface InterchangeBreakdownPanelProps {
	items: UnifiedFeeItem[];
	statementVolume?: number | null;
}

const formatCount = (count: number | null) =>
	count === null ? '—' : `${count.toLocaleString()} txns`;

const InterchangeBreakdownPanel: React.FC<InterchangeBreakdownPanelProps> = ({
	items,
	statementVolume,
}) => {
	const breakdown = useMemo(() => buildInterchangeBreakdown(items), [items]);

	const lines = [...breakdown.lines].sort(
		(a, b) =>
			CARD_TYPES.indexOf(a.cardType) - CARD_TYPES.indexOf(b.cardType) ||
			b.fees - a.fees,
	);
	const downgradeShare =
		breakdown.totalFees > 0
			? `${((breakdown.downgradeFees / breakdown.totalFees) * 100).toFixed(1)}%`
			: '0%';

	const title = (
		<h3
			id="interchange-breakdown-title"
			className="text-sm font-semibold text-gray-900"
		>
			Interchange by card type
		</h3>
	);

	if (breakdown.lines.length === 0) {
		return (
			<section
				className="mt-6 rounded-lg border border-gray-200 bg-white p-5"
				aria-labelledby="interchange-breakdown-title"
			>
				{title}
				<p className="mt-2 text-sm text-gray-600">
					This statement doesn&apos;t itemize interchange.
				</p>
			</section>
		);
	}

	return (
		<section
			className="mt-6 rounded-lg border border-gray-200 bg-white p-5"
			aria-labelledby="interchange-breakdown-title"
		>
			{title}
			<p className="mt-1 text-xs text-gray-500">
				Interchange volume {formatCurrency(breakdown.totalVolume)}
				{statementVolume
					? ` of ${formatCurrency(statementVolume)} statement volume`
					: ''}
			</p>

			<div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
				{breakdown.totals.map((total) => (
					<div key={total.cardType} className="rounded-md bg-gray-50 px-3 py-2">
						<div className="text-xs text-gray-500">{total.cardType}</div>
						<div className="text-base font-semibold text-gray-900">
							{formatCurrency(total.fees)}
						</div>
						<div className="text-xs text-gray-600">
							{formatCurrency(total.volume)} · {formatCount(total.count)}
						</div>
						<div className="text-xs text-gray-600">
							Effective rate {effectiveRate(total.fees, total.volume)}
						</div>
					</div>
				))}
			</div>

			<p className="mt-4 text-sm text-gray-700">
				Fees on downgraded categories: {formatCurrency(breakdown.downgradeFees)}{' '}
				({downgradeShare} of interchange fees).
			</p>

			<details className="mt-3">
				<summary className="cursor-pointer text-sm font-medium text-indigo-600">
					Show all {lines.length} interchange categories
				</summary>
				<table className="mt-3 w-full text-left text-sm">
					<thead className="text-xs text-gray-500">
						<tr>
							<th className="py-1 font-medium">Category</th>
							<th className="py-1 font-medium">Card type</th>
							<th className="py-1 text-right font-medium">Txns</th>
							<th className="py-1 text-right font-medium">Volume</th>
							<th className="py-1 text-right font-medium">Fees</th>
							<th className="py-1 text-right font-medium">Rate</th>
						</tr>
					</thead>
					<tbody>
						{lines.map((line, index) => (
							<tr
								key={`${line.description}-${index}`}
								className="border-t border-gray-100"
							>
								<td className="py-1 text-gray-900">
									{line.description}
									{line.downgrade && (
										<span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
											Downgrade
										</span>
									)}
								</td>
								<td className="py-1 text-gray-600">{line.cardType}</td>
								<td className="py-1 text-right">
									{line.count === null ? '—' : line.count.toLocaleString()}
								</td>
								<td className="py-1 text-right">
									{formatCurrency(line.volume)}
								</td>
								<td className="py-1 text-right">{formatCurrency(line.fees)}</td>
								<td className="py-1 text-right">
									{effectiveRate(line.fees, line.volume)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</details>

			<p className="mt-3 text-xs text-gray-500">
				Rates are as billed on the statement. Card type and downgrades are read
				from the card network and the category names; Unclassified rows name no
				card type we can read. SWITCH rows add fees but no volume, since their
				transactions are already counted under the PIN network.
			</p>
		</section>
	);
};

export default InterchangeBreakdownPanel;
